import { ref, computed } from 'vue';
import type { Theme } from '@/types';

const theme = ref<Theme>('dark');

export const useTheme = () => {
  const isDark = computed(() => theme.value === 'dark');
  const isLight = computed(() => theme.value === 'light');
  
  const setTheme = (newTheme: Theme) => {
    theme.value = newTheme;
    document.documentElement.setAttribute('data-theme', newTheme);
    localStorage.setItem('theme', newTheme);
  };
  
  const toggleTheme = () => {
    setTheme(theme.value === 'light' ? 'dark' : 'light');
  };
  
  const initTheme = () => {
    const savedTheme = localStorage.getItem('theme') as Theme;
    const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    
    const initialTheme = savedTheme || (systemPrefersDark ? 'dark' : 'light');
    setTheme(initialTheme);
    
    // Watch for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      if (!localStorage.getItem('theme')) {
        setTheme(e.matches ? 'dark' : 'light');
      }
    };
    
    mediaQuery.addEventListener('change', handleChange);
    
    // Return cleanup function
    return () => mediaQuery.removeEventListener('change', handleChange);
  };
  
  return {
    theme: computed(() => theme.value),
    isDark,
    isLight,
    setTheme,
    toggleTheme,
    initTheme,
  };
};
